import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { BrowserProvider } from "ethers";
import { CHAIN } from "../lib/format";
import { getWalletConnectProvider, disconnectWalletConnect } from "../lib/walletconnect";

const WalletCtx = createContext(null);

export function WalletProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [balance, setBalance] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [walletType, setWalletType] = useState(null); // 'injected' | 'walletconnect' | null
  const eip1193Ref = useRef(null); // active EIP-1193 provider

  const hasInjected = typeof window !== "undefined" && !!window.ethereum;
  const onBlokista = chainId === CHAIN.chainId;

  const activeProvider = () => eip1193Ref.current;
  const browserProvider = useCallback(() => {
    const p = activeProvider();
    return p ? new BrowserProvider(p) : null;
  }, []);

  const refreshBalance = useCallback(async (acct) => {
    const bp = browserProvider();
    if (!bp || !acct) return;
    try {
      const bal = await bp.getBalance(acct);
      setBalance(Number(bal) / 1e18);
    } catch { /* ignore */ }
  }, [browserProvider]);

  const syncChain = useCallback(async () => {
    const p = activeProvider();
    if (!p) return;
    try {
      const cid = await p.request({ method: "eth_chainId" });
      setChainId(parseInt(cid, 16));
    } catch { /* ignore */ }
  }, []);

  const disconnect = useCallback(async () => {
    const p = activeProvider();
    if (p) {
      try { p.removeAllListeners?.("accountsChanged"); p.removeAllListeners?.("chainChanged"); p.removeAllListeners?.("disconnect"); } catch { /* ignore */ }
    }
    if (walletType === "walletconnect") await disconnectWalletConnect();
    eip1193Ref.current = null;
    setWalletType(null);
    setAccount(null);
    setBalance(null);
    setChainId(null);
  }, [walletType]);

  const addOrSwitchChain = useCallback(async () => {
    const p = activeProvider();
    if (!p) return;
    try {
      await p.request({ method: "wallet_switchEthereumChain", params: [{ chainId: CHAIN.chainIdHex }] });
    } catch (e) {
      const msg = String(e?.message || "");
      if (e?.code === 4902 || msg.includes("Unrecognized") || msg.includes("wallet_addEthereumChain")) {
        await p.request({
          method: "wallet_addEthereumChain",
          params: [{
            chainId: CHAIN.chainIdHex, chainName: CHAIN.name,
            nativeCurrency: { name: CHAIN.symbol, symbol: CHAIN.symbol, decimals: 18 },
            rpcUrls: [CHAIN.rpcUrl], blockExplorerUrls: [window.location.origin],
          }],
        });
      }
    }
    await syncChain();
  }, [syncChain]);

  const afterConnect = useCallback(async (prov, type) => {
    eip1193Ref.current = prov;
    setWalletType(type);
    const accts = await prov.request({ method: "eth_accounts" });
    const acct = accts?.[0] || null;
    setAccount(acct);
    await addOrSwitchChain();
    await syncChain();
    await refreshBalance(acct);
    prov.on?.("accountsChanged", (a) => {
      setAccount(a?.[0] || null);
      if (a?.[0]) refreshBalance(a[0]); else setBalance(null);
    });
    prov.on?.("chainChanged", (c) => setChainId(parseInt(c, 16)));
    prov.on?.("disconnect", () => disconnect());
  }, [addOrSwitchChain, syncChain, refreshBalance, disconnect]);

  // Injected wallet (MetaMask / browser extension)
  const connect = useCallback(async () => {
    if (!hasInjected) { window.open("https://metamask.io/download/", "_blank"); return; }
    setConnecting(true);
    try {
      await window.ethereum.request({ method: "eth_requestAccounts" });
      await afterConnect(window.ethereum, "injected");
    } catch { /* user rejected */ }
    finally { setConnecting(false); }
  }, [hasInjected, afterConnect]);

  // WalletConnect v2 (QR / mobile deep-link)
  const connectWalletConnect = useCallback(async () => {
    setConnecting(true);
    try {
      const wc = await getWalletConnectProvider();
      if (!wc.session) await wc.connect(); // opens QR / wallet modal
      await afterConnect(wc, "walletconnect");
    } catch { /* user closed modal or rejected */ }
    finally { setConnecting(false); }
  }, [afterConnect]);

  // Auto-reconnect a previously-authorized injected wallet
  useEffect(() => {
    if (!hasInjected) return;
    window.ethereum.request({ method: "eth_accounts" }).then((a) => {
      if (a?.[0] && !eip1193Ref.current) {
        afterConnect(window.ethereum, "injected");
      }
    }).catch(() => {});
  }, [hasInjected, afterConnect]);

  const getSigner = useCallback(async () => {
    const bp = browserProvider();
    if (!bp) throw new Error("No wallet");
    return bp.getSigner();
  }, [browserProvider]);

  const value = {
    hasWallet: hasInjected, hasInjected, walletType, account, chainId, balance, connecting, onBlokista,
    connect, connectWalletConnect, disconnect, addOrSwitchChain, getSigner, provider: browserProvider, refreshBalance,
  };
  return <WalletCtx.Provider value={value}>{children}</WalletCtx.Provider>;
}

export function useWallet() {
  const ctx = useContext(WalletCtx);
  if (!ctx) throw new Error("useWallet must be used within WalletProvider");
  return ctx;
}
